/**
 * Regole del modulo della Scheda utente Rev05 lato interfaccia (D-07, D-10...D-21).
 *
 * Le stesse regole le verificano l'API (servizi/anagrafica.ts, rotte/persone.ts,
 * rotte/persone-sezioni.ts) e il database (292_anagrafica_rev05.sql). Qui servono a scrivere
 * l'errore vicino al campo prima dell'invio, a comporre i corpi delle richieste e a riportare sul
 * campo giusto l'errore che l'API restituisce comunque.
 *
 * Il modulo non dipende dal browser: apps/api/test/anagrafica-interfaccia.test.ts lo esegue contro
 * le rotte vere e verifica che interfaccia e API diano lo stesso esito sugli stessi dati. Una
 * regola cambiata da una parte sola fa fallire quella prova.
 */

import { consensiRegistrati, type ConsensiLetti } from './regole-consensi.ts'
import { sediDellIncarico } from './sedi.ts'

// --- cataloghi -------------------------------------------------------------------------------

/** Voce di catalogo con gli attributi che decidono le regole (290_cataloghi_rev05.sql, 292). */
export type Voce = {
  codice: string
  nome: string
  classe_dato?: string
  ammette_motivo?: boolean
  richiede_specifica?: boolean
  ammette_settore?: boolean
  esclusiva?: boolean
  eta_min?: number | null
  eta_max?: number | null
}

export type CataloghiRegole = Record<string, Voce[] | undefined>

/** Vero se la voce del catalogo ha l'attributo; falso per un codice vuoto o sconosciuto. */
export function haAttributo(
  cataloghi: CataloghiRegole, catalogo: string, codice: string, attributo: keyof Voce,
): boolean {
  if (!codice) return false
  return cataloghi[catalogo]?.find((v) => v.codice === codice)?.[attributo] === true
}

// --- stato dei moduli ------------------------------------------------------------------------

/** Tipo di persona (D-07); `operatore_cidis` e' «CIDIS Dipendenti e Collaboratori» (481). */
export type TipoPersona = 'assistito' | 'operatore_ente' | 'operatore_cidis'

/**
 * Tipi con le regole dell'operatore (D-07): chi rappresenta un ente e il personale di CIDIS (481).
 * Come tipoOperatore dell'API (servizi/anagrafica.ts).
 */
export const tipoOperatore = (tipo: string | null | undefined): boolean =>
  tipo === 'operatore_ente' || tipo === 'operatore_cidis'

// --- dati anonimizzati (481) -------------------------------------------------------------------

/**
 * Iniziali di un nome o di un cognome sulla scheda con dati anonimizzati: da una a tre lettere,
 * ciascuna eventualmente seguita da un punto, separate o no da uno spazio («M.», «M. A.», «MA»).
 * Come servizi/anonimizzazione.ts dell'API.
 */
const INIZIALI = /^\p{L}\.?(?: ?\p{L}\.?){0,2}$/u

export const normalizzaIniziali = (testo: string) => testo.trim().replace(/\s+/g, ' ').toLocaleUpperCase('it-IT')
export const sonoIniziali = (testo: string) => INIZIALI.test(normalizzaIniziali(testo))

/** Dati identificativi che impediscono di attivare «Dati anonimizzati» (core.persona_dati_identificativi). */
export const NOMI_DATI_IDENTIFICATIVI: Record<string, string> = {
  codice_fiscale: 'codice fiscale',
  telefono: 'telefono',
  email: 'email',
  indirizzo: 'indirizzo di residenza o di domicilio',
  data_nascita: 'data di nascita completa (basta l’anno)',
  luogo_nascita: 'comune o località di nascita',
  documento: 'tipo o numero del documento',
  scadenza_permesso: 'scadenza del titolo di soggiorno',
  altri_codici: 'altri codici (STP, CUI…)',
  documenti_identita: 'documenti d’identità o moduli firmati caricati',
}

/** Comune scelto da una ricerca: il testo digitato vale solo se corrisponde a un codice ISTAT. */
export type ComuneScelto = { codice: string; testo: string; provincia: string }

/** Ente scelto da una ricerca; la tipologia e' la categoria sintetica dell'ente (D-07). */
export type EnteScelto = { id: string; testo: string; tipologia: string }

/**
 * Come si conosce la nascita: data completa, solo anno (Q-E04) oppure non nota, e allora la
 * fascia d'eta' si indica a mano (D-21).
 */
export type ModoNascita = 'data' | 'anno' | 'ignota'

export const comuneVuoto = (): ComuneScelto => ({ codice: '', testo: '', provincia: '' })
export const enteVuoto = (): EnteScelto => ({ id: '', testo: '', tipologia: '' })

/** Intestazione, come e' venuto a conoscenza e dati anagrafici: una sola rotta, PATCH /persone/:id. */
export type StatoAnagrafica = {
  tipo: TipoPersona
  /**
   * Scheda con dati anonimizzati (481): nome e cognome sono iniziali, nessun dato identificativo,
   * il motivo dell'assenza del codice fiscale lo scrive l'API.
   */
  dati_anonimizzati: boolean
  sede_riferimento: string
  canali_conoscenza: string[]
  canale_specifica: string
  cognome: string
  nome: string
  genere_codice: string
  paese_nascita: string
  comune_nascita: ComuneScelto
  luogo_nascita_estero: string
  modo_nascita: ModoNascita
  data_nascita: string
  anno_nascita: string
  /** Precisione registrata: con la data completa si conservano «stimata» e «da verificare». */
  precisione_registrata: string
  /**
   * 460: chi modifica conferma di aver controllato la data «da verificare» (o che non e' nota). Con
   * la spunta la precisione diventa esatta; cambiare la data da verificare senza spunta non si salva.
   */
  nascita_verificata: boolean
  fascia_eta_manuale: string
  cittadinanza: string
  residenza_comune: ComuneScelto
  residenza_indirizzo: string
  domicilio_diverso: boolean
  domicilio_comune: ComuneScelto
  domicilio_indirizzo: string
  telefono: string
  email: string
  cf: string
  /** Voce di ref.motivo_assenza_cf (453); il testo e' la specifica di «in attesa di altro». */
  motivo_assenza_cf_codice: string
  motivo_assenza_cf: string
  lingua_madre: string
  lingue_veicolari: string[]
  /** Conoscenza della lingua italiana: dato della persona dal 17/10/2026, obbligatorio per chi e' assistito. */
  livello_italiano: string
  ente_appartenenza: EnteScelto
  /**
   * Sede CIDIS di appartenenza (N-2): alternativa all'ente, per chi rappresenta CIDIS stessa.
   * Vale l'una o l'altra, mai tutte e due: rispondono alla stessa domanda.
   */
  sede_appartenenza: string
  ente_operativo: EnteScelto
  ambito_professionale: string
  ambito_professionale_specifica: string
}

/** Condizione giuridica: dato particolare, si scrive con persona.modifica_particolari. */
export type StatoCondizione = {
  condizione_giuridica: string
  motivo_permesso: string
  permesso_scadenza: string
}

/** Formazione – Lavoro: ogni salvataggio e' una rilevazione nuova (core.situazione_lavorativa). */
export type StatoFormazione = {
  anni_studio: string
  titolo_studio: string
  /** Il titolo e' riconosciuto in Italia? Si chiede solo con un titolo diverso da «nessuno». */
  riconoscimento_titolo: '' | 'si' | 'no' | 'in_corso'
  situazione: string
  situazione_specifica: string
  settore: string
  settore_specifica: string
  lavoro_paese_origine: string
  lavoro_paese_origine_specifica: string
  /**
   * Campi dell'ultima rilevazione che la Rev05 non chiede (dati del vecchio archivio): si
   * ricopiano nella rilevazione nuova, altrimenti un salvataggio li farebbe sparire dalla scheda.
   */
  conservati: Record<string, string | number | boolean>
}

/** Servizi bancari e finanziari (D-18): PUT /persone/:id/servizi-bancari. */
export type StatoServizi = { voci: string[]; motivo: string; motivo_specifica: string }

/** Condizione abitativa e familiare: ogni salvataggio e' una rilevazione nuova. */
export type StatoAbitativa = {
  stato_civile: string
  figli_italia_totale: string
  figli_italia_minorenni: string
  figli_italia_maggiorenni: string
  figli_estero_totale: string
  figli_estero_minorenni: string
  figli_estero_maggiorenni: string
  soluzione_abitativa: string
  nucleo_familiare: string
  nucleo_familiare_specifica: string
  persone_conviventi: string
}

export type StatiScheda = {
  anagrafica: StatoAnagrafica
  condizione: StatoCondizione
  formazione: StatoFormazione
  servizi: StatoServizi
  abitativa: StatoAbitativa
}

export function statiNuovi(sede = ''): StatiScheda {
  return {
    anagrafica: {
      tipo: 'assistito', dati_anonimizzati: false, sede_riferimento: sede, canali_conoscenza: [], canale_specifica: '',
      cognome: '', nome: '', genere_codice: '', paese_nascita: '', comune_nascita: comuneVuoto(),
      luogo_nascita_estero: '', modo_nascita: 'data', data_nascita: '', anno_nascita: '',
      precisione_registrata: 'esatta', nascita_verificata: false, fascia_eta_manuale: '', cittadinanza: '',
      residenza_comune: comuneVuoto(), residenza_indirizzo: '', domicilio_diverso: false,
      domicilio_comune: comuneVuoto(), domicilio_indirizzo: '', telefono: '', email: '', cf: '',
      motivo_assenza_cf_codice: '', motivo_assenza_cf: '', lingua_madre: '', lingue_veicolari: [],
      livello_italiano: '', ente_appartenenza: enteVuoto(),
      sede_appartenenza: '',
      ente_operativo: enteVuoto(), ambito_professionale: '', ambito_professionale_specifica: '',
    },
    condizione: { condizione_giuridica: '', motivo_permesso: '', permesso_scadenza: '' },
    formazione: {
      anni_studio: '', titolo_studio: '', riconoscimento_titolo: '', situazione: '', situazione_specifica: '',
      settore: '', settore_specifica: '', lavoro_paese_origine: '', lavoro_paese_origine_specifica: '',
      conservati: {},
    },
    servizi: { voci: [], motivo: '', motivo_specifica: '' },
    abitativa: {
      stato_civile: '', figli_italia_totale: '', figli_italia_minorenni: '', figli_italia_maggiorenni: '',
      figli_estero_totale: '', figli_estero_minorenni: '', figli_estero_maggiorenni: '',
      soluzione_abitativa: '', nucleo_familiare: '', nucleo_familiare_specifica: '', persone_conviventi: '',
    },
  }
}

// --- scheda letta dall'API -------------------------------------------------------------------

export type LuogoLetto = { comune: string; nome: string; provincia_sigla: string | null; provincia_nome: string | null }
export type EnteLetto = { id: string; sigla: string | null; nome: string; categoria_ente: string | null; categoria_ente_nome: string | null }

export type VulnerabilitaLetta = {
  vulnerabilita: string; nome: string; classe_dato: string; rilevata_il: string; conclusa_il: string | null
}

/**
 * Risposta di GET /persone/:id per le parti che l'anagrafica usa. I campi facoltativi mancano
 * sulla scheda mascherata o a chi non ha il permesso della classe del dato.
 */
export type SchedaLetta = {
  id: string
  numero_scheda: number
  etichetta: string
  mascherata: boolean
  codice_storico: string | null
  tipo: TipoPersona
  tipo_nome: string
  stato: string
  ha_area_riservata: boolean
  /** Scheda con dati anonimizzati (481). */
  dati_anonimizzati?: boolean
  /**
   * Solo a chi modifica la scheda in chiaro, se non e' gia' anonimizzata: i dati identificativi che
   * impediscono di attivare «Dati anonimizzati» (codici di NOMI_DATI_IDENTIFICATIVI).
   */
  anonimizzazione?: { ostacoli: string[] }
  /** La migrazione l'ha segnata da verificare; `cause_verifica` dice perche' (460). */
  da_verificare: boolean
  cause_verifica?: string[]
  /** Dati che mancano, come codici di core.dati_mancanti (460): assente nella stampa. */
  dati_mancanti?: { obbligatori: string[]; consigliati: string[] }
  creata_il: string
  nome?: string
  cognome?: string
  genere_codice: string | null
  data_nascita: string | null
  precisione_nascita: string
  paese_nascita: string | null
  comune_nascita?: string | null
  comune_nascita_nome?: string | null
  luogo_nascita_estero?: string | null
  cittadinanza: string | null
  cf?: string | null
  motivo_assenza_cf: string | null
  motivo_assenza_cf_codice?: string | null
  motivo_assenza_cf_nome?: string | null
  livello_italiano?: string | null
  livello_italiano_nome?: string | null
  email?: string | null
  telefono?: string | null
  residenza_indirizzo?: string | null
  domicilio_indirizzo?: string | null
  residenza?: LuogoLetto | null
  domicilio?: LuogoLetto | null
  domicilio_diverso?: boolean
  condizione_giuridica?: string | null
  motivo_permesso?: string | null
  /** Solo a chi modifica la scheda in chiaro: vero se condizione o motivo sono registrati, senza il valore. */
  condizione_giuridica_registrata?: boolean
  permesso_scadenza: string | null
  sede_riferimento: string | null
  lingua_madre?: string | null
  lingua_madre_nome?: string | null
  lingue_veicolari?: { codice: string; nome: string }[]
  canali_conoscenza?: { codice: string; nome: string; specifica: string | null }[]
  operatore?: {
    ente_appartenenza: EnteLetto | null
    sede_appartenenza: { id: string; nome: string | null; codice: string | null } | null
    tipologia_ente: { codice: string; nome: string | null } | null
    ente_operativo: EnteLetto | null
    ambito_professionale: { codice: string; nome: string | null } | null
    ambito_professionale_specifica: string | null
  } | null
  fascia_eta?: string | null
  fascia_eta_manuale?: string | null
  formazione_lavoro?: Record<string, unknown> | null
  condizione_abitativa?: Record<string, unknown> | null
  servizi_bancari?: {
    voci: { codice: string; nome: string; rilevato_il: string }[]
    motivo: { codice: string; nome: string | null } | null
    motivo_specifica: string | null
  }
  vulnerabilita?: VulnerabilitaLetta[]
  categorie: { categoria: string; nome: string; confermata: boolean; decisa_il: string | null }[]
  categorie_proposte: { categoria: string; nome: string; motivo: string }[]
  intestazione: {
    numero_scheda: number
    codice_storico: string | null
    /** Schede unite a questa con «Unisci schede» (473): numero e codice dell'archivio precedente. */
    schede_unite?: { numero_scheda: number; codice_storico: string | null; unita_il: string | null }[]
    sede: { id: string; codice: string | null; nome: string } | null
    aree: { codice: string; nome: string; aperta: boolean }[]
    progetti: { id: string; codice_cidis: string | null; titolo: string }[]
    anno: number
    mese: number
    /** Chi l'ha inserita, «Nome Cognome» (Osservazioni 4): nullo se la scheda non ha l'autore. */
    inserita_da?: string | null
  }
  puo: {
    modificare: boolean
    modificareParticolari: boolean
    /** Falso: codice fiscale e numero del documento arrivano mascherati. */
    vedereIdentificativi: boolean
    vedereRiservato: boolean
    scrivereRiservato: boolean
    registrareIntervento: boolean
    /** Aprire un percorso con il suo primo intervento (19/10/2026). */
    aprirePercorso?: boolean
    /** Consensi (rotte/consensi.ts): lettura e registrazione sulla scheda, stampa della scheda in chiaro. */
    leggereConsensi?: boolean
    registrareConsensi?: boolean
    stampare?: boolean
    /** «Unisci con un'altra scheda» (473): la Direzione, sulle schede che legge in chiaro. */
    unire?: boolean
  }
  /** Assente senza `consenso.leggi` sulla scheda. */
  consensi?: ConsensiLetti
}

// --- permessi sulla scheda e sulla sede ------------------------------------------------------

type PermessiScheda = SchedaLetta['puo']

/**
 * Voce di categoria di utenti che l'utente puo' confermare o escludere (D-21), con la regola
 * dell'API (servizi/anagrafica.ts, PERMESSO_SCRITTURA_CLASSE e lettoreDiClasse): si scrive la classe
 * del dato e la si legge. Una voce riservata si legge con `riservato.leggi` sulla scheda oppure, se
 * l'ha decisa l'utente, perche' la scheda gliela mostra: `visibile` e' vero per le voci che la
 * scheda restituisce (confermate o proposte). OPI ha `riservato.scrivi` su tutta la sede ma
 * `riservato.leggi` solo sulle schede che ha creato: sulle altre non gli si propone Detenuti.
 */
export function scriveCategoria(puo: PermessiScheda, classe: string | undefined, visibile: boolean): boolean {
  if (classe === 'riservato') return puo.scrivereRiservato && (puo.vedereRiservato || visibile)
  if (classe === 'particolare' || classe === 'giudiziario') return puo.modificareParticolari
  return puo.modificare
}

/**
 * Voce del Vulnus che l'utente puo' registrare (`visibile` falso) o concludere e correggere
 * (`visibile` vero, perche' la scheda la restituisce): tutte chiedono `persona.modifica_particolari`,
 * le riservate anche `riservato.scrivi` e la lettura della classe, come le categorie.
 */
export function scriveVulnerabilita(puo: PermessiScheda, classe: string | undefined, visibile: boolean): boolean {
  return puo.modificareParticolari && (classe !== 'riservato' || scriveCategoria(puo, classe, visibile))
}

/**
 * Passaggio a «Operatore» (D-07): la condizione giuridica registrata va tolta nella stessa PATCH,
 * che allora chiede `persona.modifica_particolari`. Chi non lo ha non puo' eseguire il passaggio:
 * l'API risponderebbe 400 SEZIONE_NON_PREVISTA chiedendo di togliere dati che non vede.
 */
export function condizionePerOperatore(s: SchedaLetta): { presente: boolean; bloccato: boolean } {
  const presente = s.condizione_giuridica_registrata ?? !!(s.condizione_giuridica || s.motivo_permesso)
  return { presente, bloccato: presente && !s.puo.modificareParticolari }
}

/** Incarico della sessione (GET /auth/io) per le parti che servono ai permessi sulla sede. */
export type IncaricoSessione = {
  permessi: string[]; ambito: { tipo: string; id: string | null; sedi?: string[] }
}

/**
 * Permesso valido sulla sede scelta, come lo verifica l'API con il perimetro `{ sede }`
 * (contesto.ts, copre): vale un incarico di organizzazione, o della stessa sede o della sua sede
 * madre se e' uno sportello (463); un incarico di area, progetto o tipologia non copre una sede.
 * L'unione dei permessi della sessione non basta: un utente OPI su una sede ed EXT su un'altra non
 * scrive i dati particolari sulla seconda.
 */
export function permessoSullaSede(incarichi: IncaricoSessione[], permesso: string, sede: string): boolean {
  return incarichi.some((i) => i.permessi.includes(permesso)
    && (i.ambito.tipo === 'organizzazione' || (!!sede && sediDellIncarico(i).includes(sede))))
}

/** Sezione che la creazione non ha registrato dopo POST /persone (NuovaPersona.tsx → Scheda.tsx). */
export type SezioneNonRegistrata = { sezione: 'formazione' | 'servizi' | 'abitativa' | 'consensi'; testo: string }

/**
 * Sezioni ancora da registrare. L'avviso nasce alla creazione e non va ripetuto quando non e' piu'
 * vero: una sezione completata dalla linguetta Anagrafica esce dall'elenco; per un operatore
 * formazione – lavoro, servizi bancari e condizione abitativa non esistono (D-07), i consensi si'.
 */
export function sezioniMancanti(avvisi: SezioneNonRegistrata[], s: SchedaLetta): SezioneNonRegistrata[] {
  return avvisi.filter((a) => (a.sezione === 'consensi' ? !consensiRegistrati(s.consensi)
    : tipoOperatore(s.tipo) ? false
    : a.sezione === 'formazione' ? !s.formazione_lavoro
    : a.sezione === 'servizi' ? !s.servizi_bancari?.voci.length
      : !s.condizione_abitativa))
}

/** Giorno di una data arrivata come testo ISO: le colonne date escono come mezzanotte UTC. */
export const giornoIso = (valore: unknown): string => (valore ? String(valore).slice(0, 10) : '')

const testoDi = (valore: unknown): string => (valore === null || valore === undefined ? '' : String(valore))

export const etichettaEnte = (e: { nome: string; sigla: string | null }) => (e.sigla ? `${e.nome} (${e.sigla})` : e.nome)

const comuneDa = (luogo: LuogoLetto | null | undefined): ComuneScelto => (luogo
  ? { codice: luogo.comune, testo: luogo.provincia_sigla ? `${luogo.nome} (${luogo.provincia_sigla})` : luogo.nome,
      provincia: luogo.provincia_sigla ?? '' }
  : comuneVuoto())

/** Campi della rilevazione lavorativa conservati tra una rilevazione e l'altra, con i limiti dell'API. */
const CONSERVATI_NUMERICI: Record<string, number> = { anni_studio_italia: 40, anni_studio_estero: 40, tirocini_svolti: 50 }

/** Stato dei moduli ricavato dalla scheda letta: e' il punto di partenza di ogni modifica. */
export function statoDaScheda(s: SchedaLetta): StatiScheda {
  const vuoti = statiNuovi()
  const op = s.operatore ?? null
  const modo: ModoNascita = !s.data_nascita ? 'ignota' : s.precisione_nascita === 'solo_anno' ? 'anno' : 'data'
  const lavoro = s.formazione_lavoro ?? {}
  const casa = s.condizione_abitativa ?? {}

  const conservati: Record<string, string | number | boolean> = {}
  for (const [campo, massimo] of Object.entries(CONSERVATI_NUMERICI)) {
    const v = lavoro[campo]
    if (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= massimo) conservati[campo] = v
  }
  const titoloEstero = lavoro.titolo_estero_non_riconosciuto
  if (typeof titoloEstero === 'string' && titoloEstero.trim() && titoloEstero.length <= 200) {
    conservati.titolo_estero_non_riconosciuto = titoloEstero
  }
  if (typeof lavoro.titolo_estero_in_riconoscimento === 'boolean') {
    conservati.titolo_estero_in_riconoscimento = lavoro.titolo_estero_in_riconoscimento
  }

  return {
    anagrafica: {
      ...vuoti.anagrafica,
      tipo: s.tipo,
      dati_anonimizzati: !!s.dati_anonimizzati,
      sede_riferimento: s.sede_riferimento ?? '',
      canali_conoscenza: (s.canali_conoscenza ?? []).map((c) => c.codice),
      canale_specifica: (s.canali_conoscenza ?? []).find((c) => c.specifica)?.specifica ?? '',
      cognome: s.cognome ?? '',
      nome: s.nome ?? '',
      genere_codice: s.genere_codice ?? '',
      paese_nascita: s.paese_nascita ?? '',
      comune_nascita: s.comune_nascita
        ? { codice: s.comune_nascita, testo: s.comune_nascita_nome ?? s.comune_nascita, provincia: '' }
        : comuneVuoto(),
      luogo_nascita_estero: s.luogo_nascita_estero ?? '',
      modo_nascita: modo,
      data_nascita: modo === 'data' ? giornoIso(s.data_nascita) : '',
      anno_nascita: modo === 'anno' ? giornoIso(s.data_nascita).slice(0, 4) : '',
      precisione_registrata: s.precisione_nascita,
      nascita_verificata: false,
      fascia_eta_manuale: s.fascia_eta_manuale ?? '',
      cittadinanza: s.cittadinanza ?? '',
      residenza_comune: comuneDa(s.residenza),
      residenza_indirizzo: s.residenza_indirizzo ?? '',
      domicilio_diverso: !!s.domicilio_diverso,
      domicilio_comune: comuneDa(s.domicilio),
      domicilio_indirizzo: s.domicilio_indirizzo ?? '',
      telefono: s.telefono ?? '',
      email: s.email ?? '',
      cf: s.cf ?? '',
      motivo_assenza_cf_codice: s.motivo_assenza_cf_codice ?? '',
      // Per le voci senza specifica il testo e' il nome della voce, scritto dall'API: non e' una
      // specifica da mostrare nel campo.
      motivo_assenza_cf: s.motivo_assenza_cf_codice === 'ATTESA_ALTRO' ? s.motivo_assenza_cf ?? '' : '',
      lingua_madre: s.lingua_madre ?? '',
      lingue_veicolari: (s.lingue_veicolari ?? []).map((l) => l.codice),
      livello_italiano: s.livello_italiano ?? '',
      ente_appartenenza: op?.ente_appartenenza
        ? { id: op.ente_appartenenza.id, testo: etichettaEnte(op.ente_appartenenza), tipologia: op.tipologia_ente?.nome ?? '' }
        : enteVuoto(),
      sede_appartenenza: op?.sede_appartenenza?.id ?? '',
      ente_operativo: op?.ente_operativo
        ? { id: op.ente_operativo.id, testo: etichettaEnte(op.ente_operativo),
            tipologia: op.ente_operativo.categoria_ente_nome ?? '' }
        : enteVuoto(),
      ambito_professionale: op?.ambito_professionale?.codice ?? '',
      ambito_professionale_specifica: op?.ambito_professionale_specifica ?? '',
    },
    condizione: {
      condizione_giuridica: s.condizione_giuridica ?? '',
      motivo_permesso: s.motivo_permesso ?? '',
      permesso_scadenza: giornoIso(s.permesso_scadenza),
    },
    formazione: {
      anni_studio: testoDi(lavoro.anni_studio),
      titolo_studio: testoDi(lavoro.titolo_studio),
      riconoscimento_titolo: (['si', 'no', 'in_corso'] as const).find((v) => v === lavoro.riconoscimento_titolo) ?? '',
      situazione: testoDi(lavoro.situazione),
      situazione_specifica: testoDi(lavoro.situazione_specifica),
      settore: testoDi(lavoro.settore),
      settore_specifica: testoDi(lavoro.settore_specifica),
      lavoro_paese_origine: testoDi(lavoro.lavoro_paese_origine),
      lavoro_paese_origine_specifica: testoDi(lavoro.lavoro_paese_origine_specifica),
      conservati,
    },
    servizi: {
      voci: (s.servizi_bancari?.voci ?? []).map((v) => v.codice),
      motivo: s.servizi_bancari?.motivo?.codice ?? '',
      motivo_specifica: s.servizi_bancari?.motivo_specifica ?? '',
    },
    abitativa: {
      stato_civile: testoDi(casa.stato_civile),
      figli_italia_totale: testoDi(casa.figli_italia_totale),
      figli_italia_minorenni: testoDi(casa.figli_italia_minorenni),
      figli_italia_maggiorenni: testoDi(casa.figli_italia_maggiorenni),
      figli_estero_totale: testoDi(casa.figli_estero_totale),
      figli_estero_minorenni: testoDi(casa.figli_estero_minorenni),
      figli_estero_maggiorenni: testoDi(casa.figli_estero_maggiorenni),
      soluzione_abitativa: testoDi(casa.soluzione_abitativa),
      nucleo_familiare: testoDi(casa.nucleo_familiare),
      nucleo_familiare_specifica: testoDi(casa.nucleo_familiare_specifica),
      persone_conviventi: testoDi(casa.persone_conviventi),
    },
  }
}

// --- verifiche -------------------------------------------------------------------------------

/** Errori per campo: la chiave e' il nome del campo nell'API, il valore il messaggio da mostrare. */
export type Errori = Record<string, string>

export const nessunErrore = (errori: Errori) => Object.keys(errori).length === 0

/** Intero non negativo scritto in un campo: null se vuoto, NaN se non e' un intero. */
export function intero(valore: string): number | null {
  const testo = valore.trim()
  if (!testo) return null
  return /^\d+$/.test(testo) ? Number(testo) : Number.NaN
}

const pieno = (valore: string) => valore.trim() !== ''
const oppureNullo = (valore: string) => valore.trim() || null

/** Data di oggi nel fuso del dispositivo, come AAAA-MM-GG. */
export function oggiLocale(adesso = new Date()): string {
  return new Date(adesso.getTime() - adesso.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}

/** Eta' compiuta alla data indicata; null senza data o con precisione «ignota». */
export function etaAllaData(dataNascita: string | null | undefined, precisione: string, alla: string): number | null {
  if (!dataNascita || precisione === 'ignota') return null
  const [a, m, g] = dataNascita.slice(0, 10).split('-').map(Number)
  const [oa, om, og] = alla.slice(0, 10).split('-').map(Number)
  if ([a, m, g, oa, om, og].some((n) => Number.isNaN(n))) return null
  return oa - a - (om < m || (om === m && og < g) ? 1 : 0)
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Limite dell'API (rotte/persone.ts, `lingue_veicolari: z.array(...).max(10)`). */
export const MASSIMO_LINGUE_VEICOLARI = 10

/** Comune scritto ma non scelto dall'elenco: senza codice ISTAT il campo non si salverebbe. */
function comuneNonScelto(errori: Errori, campo: string, comune: ComuneScelto) {
  if (pieno(comune.testo) && !comune.codice) errori[campo] = 'Scegliere il comune dall’elenco.'
}

export function verificaAnagrafica(
  s: StatoAnagrafica, cataloghi: CataloghiRegole,
  opzioni: { sedeObbligatoria: boolean; oggi: string; dataIniziale?: string },
): Errori {
  const e: Errori = {}
  if (!pieno(s.cognome)) e.cognome = s.dati_anonimizzati ? 'Indicare le iniziali del cognome.' : 'Indicare il cognome.'
  if (!pieno(s.nome)) e.nome = s.dati_anonimizzati ? 'Indicare le iniziali del nome.' : 'Indicare il nome.'
  if (opzioni.sedeObbligatoria && !s.sede_riferimento) e.sede_riferimento = 'Indicare la sede del servizio.'
  // 481: con i dati anonimizzati solo iniziali, nessun dato identificativo, nascita per anno o fascia.
  if (s.dati_anonimizzati) {
    const iniziali = 'Solo le iniziali: da una a tre lettere, anche con il punto («M.», «M. A.», «MA»).'
    if (pieno(s.cognome) && !sonoIniziali(s.cognome)) e.cognome = iniziali
    if (pieno(s.nome) && !sonoIniziali(s.nome)) e.nome = iniziali
    if (s.tipo !== 'assistito') e.tipo = 'I dati anonimizzati valgono solo per «Migrante o con background migratorio».'
    if (s.modo_nascita === 'data') e.modo_nascita = 'Con i dati anonimizzati si indica solo l’anno, oppure «non nota» con la fascia d’età.'
  }

  comuneNonScelto(e, 'comune_nascita', s.comune_nascita)
  if (s.comune_nascita.codice && s.paese_nascita !== 'ITA') {
    e.comune_nascita = 'Il comune di nascita si indica solo se il Paese d’origine è l’Italia.'
  }
  if (pieno(s.luogo_nascita_estero) && (!s.paese_nascita || s.paese_nascita === 'ITA')) {
    e.luogo_nascita_estero = 'La località di nascita in testo si indica solo per chi è nato all’estero.'
  }

  const annoCorrente = Number(opzioni.oggi.slice(0, 4))
  if (s.modo_nascita === 'data') {
    if (!s.data_nascita) e.data_nascita = 'Indicare la data di nascita, oppure scegliere «solo l’anno» o «non nota».'
    else if (s.data_nascita > opzioni.oggi) e.data_nascita = 'La data di nascita non può essere successiva a oggi.'
    // 460: la data del vecchio archivio era da verificare; cambiandola si conferma di averla controllata.
    else if (s.precisione_registrata === 'da_verificare' && opzioni.dataIniziale !== undefined
             && s.data_nascita !== opzioni.dataIniziale && !s.nascita_verificata) {
      e.data_nascita = 'Prima di salvare la data nuova, spunta «Ho controllato la data su un documento».'
    }
  } else if (s.modo_nascita === 'anno') {
    const anno = intero(s.anno_nascita)
    if (anno === null) e.anno_nascita = 'Indicare l’anno di nascita.'
    else if (Number.isNaN(anno) || anno < 1900 || anno > annoCorrente) {
      e.anno_nascita = `Anno di nascita tra 1900 e ${annoCorrente}.`
    }
  }

  comuneNonScelto(e, 'residenza_comune', s.residenza_comune)
  if (s.domicilio_diverso) comuneNonScelto(e, 'domicilio_comune', s.domicilio_comune)
  if (pieno(s.email) && !EMAIL.test(s.email.trim())) e.email = 'Indirizzo email non valido.'
  if (!pieno(s.cf) && !s.dati_anonimizzati) {
    if (!s.motivo_assenza_cf_codice) e.motivo_assenza_cf_codice = 'Senza codice fiscale, scegli il motivo.'
    else if (haAttributo(cataloghi, 'motivo_assenza_cf', s.motivo_assenza_cf_codice, 'richiede_specifica')
             && !pieno(s.motivo_assenza_cf)) {
      e.motivo_assenza_cf = 'Scrivi in attesa di che cosa.'
    }
  }
  if (s.tipo === 'assistito' && !s.livello_italiano) {
    e.livello_italiano = 'Indica quanto conosce l’italiano.'
  }
  if (s.lingue_veicolari.length > MASSIMO_LINGUE_VEICOLARI) {
    e.lingue_veicolari = `Al massimo ${MASSIMO_LINGUE_VEICOLARI} lingue.`
  }

  if (tipoOperatore(s.tipo)) {
    // N-2 corregge D-07: chi rappresenta CIDIS appartiene a una sede, perche' i record che
    // registravano le sedi CIDIS come enti sono usciti dall'anagrafica degli enti.
    if (s.ente_appartenenza.id && s.sede_appartenenza) {
      e.sede_appartenenza = 'Si indica l’ente di appartenenza oppure la sede CIDIS, non entrambi.'
    } else if (pieno(s.ente_appartenenza.testo) && !s.ente_appartenenza.id) {
      e.ente_appartenenza_id = 'Scegliere l’ente dall’elenco.'
    } else if (!s.ente_appartenenza.id && !s.sede_appartenenza) {
      e.ente_appartenenza_id = 'Per un operatore serve l’ente di appartenenza, oppure la sede CIDIS.'
    }
    if (pieno(s.ente_operativo.testo) && !s.ente_operativo.id) e.ente_operativo_id = 'Scegliere l’ente dall’elenco.'
    if (pieno(s.ambito_professionale_specifica)
        && !haAttributo(cataloghi, 'ambito_professionale', s.ambito_professionale, 'richiede_specifica')) {
      e.ambito_professionale_specifica = 'Il testo si indica solo per l’ambito professionale «altro».'
    }
  }
  return e
}

/**
 * Condizione giuridica (D-16). Come nel database, la regola del motivo vale quando cambiano
 * condizione o motivo: una scheda migrata con un motivo non ammesso si modifica nel resto.
 */
export function verificaCondizione(s: StatoCondizione, cataloghi: CataloghiRegole, iniziale?: StatoCondizione): Errori {
  const e: Errori = {}
  const cambiati = !iniziale || iniziale.condizione_giuridica !== s.condizione_giuridica
    || iniziale.motivo_permesso !== s.motivo_permesso
  if (cambiati && s.motivo_permesso
      && !haAttributo(cataloghi, 'condizione_giuridica', s.condizione_giuridica, 'ammette_motivo')) {
    e.motivo_permesso = 'Il motivo si indica solo per le condizioni giuridiche che lo prevedono.'
  }
  return e
}

function interoTra(e: Errori, campo: string, valore: string, massimo: number) {
  const n = intero(valore)
  if (n !== null && (Number.isNaN(n) || n > massimo)) e[campo] = `Numero intero da 0 a ${massimo}.`
}

export function verificaFormazione(s: StatoFormazione, cataloghi: CataloghiRegole): Errori {
  const e: Errori = {}
  interoTra(e, 'anni_studio', s.anni_studio, 40)
  if (pieno(s.situazione_specifica)
      && !haAttributo(cataloghi, 'situazione_lavorativa', s.situazione, 'richiede_specifica')) {
    e.situazione_specifica = 'Il testo si indica solo per la situazione «altro (specificare)».'
  }
  if (s.settore && !haAttributo(cataloghi, 'situazione_lavorativa', s.situazione, 'ammette_settore')) {
    e.settore = 'Il settore si indica solo per occupato/a e lavoratore autonomo.'
  }
  if (pieno(s.settore_specifica) && !haAttributo(cataloghi, 'settore_lavorativo', s.settore, 'richiede_specifica')) {
    e.settore_specifica = 'Il testo si indica solo per il settore «altro».'
  }
  if (pieno(s.lavoro_paese_origine_specifica)
      && !haAttributo(cataloghi, 'lavoro_paese_origine', s.lavoro_paese_origine, 'richiede_specifica')) {
    e.lavoro_paese_origine_specifica = 'Il testo si indica solo per «altro (specificare)».'
  }
  return e
}

/** Voce esclusiva scelta («NESSUNO di questi servizi»), se c'e'. */
export const voceEsclusiva = (s: StatoServizi, cataloghi: CataloghiRegole) =>
  s.voci.find((codice) => haAttributo(cataloghi, 'strumento_finanziario', codice, 'esclusiva')) ?? null

export function verificaServizi(s: StatoServizi, cataloghi: CataloghiRegole): Errori {
  const e: Errori = {}
  const esclusiva = voceEsclusiva(s, cataloghi)
  if (esclusiva && s.voci.length > 1) e.voci = '«NESSUNO di questi servizi» esclude le altre voci.'
  if (s.motivo && !esclusiva) e.motivo = 'Il motivo si indica solo con «NESSUNO di questi servizi».'
  if (pieno(s.motivo_specifica)
      && !haAttributo(cataloghi, 'motivo_nessun_servizio_bancario', s.motivo, 'richiede_specifica')) {
    e.motivo_specifica = 'Il testo si indica solo per «Altro motivo».'
  }
  return e
}

export function verificaAbitativa(s: StatoAbitativa, cataloghi: CataloghiRegole): Errori {
  const e: Errori = {}
  const figli = ['figli_italia_totale', 'figli_italia_minorenni', 'figli_italia_maggiorenni',
    'figli_estero_totale', 'figli_estero_minorenni', 'figli_estero_maggiorenni'] as const
  for (const campo of figli) interoTra(e, campo, s[campo], 50)
  interoTra(e, 'persone_conviventi', s.persone_conviventi, 50)

  // D-19: il totale non e' inferiore alla somma delle due fasce.
  for (const dove of ['italia', 'estero'] as const) {
    const campo = `figli_${dove}_totale` as const
    if (e[campo]) continue
    const totale = intero(s[campo])
    const minori = intero(s[`figli_${dove}_minorenni`]) ?? 0
    const maggiori = intero(s[`figli_${dove}_maggiorenni`]) ?? 0
    if (totale !== null && !Number.isNaN(minori + maggiori) && totale < minori + maggiori) {
      e[campo] = `Il totale non può essere inferiore alla somma delle fasce (${minori + maggiori}).`
    }
  }

  const conviventi = intero(s.persone_conviventi)
  if (!e.persone_conviventi && s.nucleo_familiare === 'SOLO_A' && conviventi !== null && conviventi > 0) {
    e.persone_conviventi = 'Con «solo/a» le persone conviventi sono zero.'
  }
  if (pieno(s.nucleo_familiare_specifica)
      && !haAttributo(cataloghi, 'nucleo_familiare', s.nucleo_familiare, 'richiede_specifica')) {
    e.nucleo_familiare_specifica = 'Il testo si indica solo per «Con chi vive: altro».'
  }
  return e
}

export type StatoVulnerabilita = { codice: string; rilevata_il: string; conclusa_il: string }

export function verificaVulnerabilita(s: StatoVulnerabilita, oggi: string): Errori {
  const e: Errori = {}
  if (!s.codice) e.codice = 'Scegliere la voce del profilo di vulnerabilità.'
  if (!s.rilevata_il) e.rilevata_il = 'Indicare la data di rilevazione.'
  else if (s.rilevata_il > oggi) e.rilevata_il = 'La data di rilevazione non può essere successiva a oggi.'
  if (s.conclusa_il && s.conclusa_il < (s.rilevata_il || oggi)) {
    e.conclusa_il = 'La data di conclusione non può precedere quella di rilevazione.'
  }
  return e
}

/**
 * Categorie da confermare (D-21, CRIT-struttura-11): una categoria con soglie d'eta' si conferma
 * solo se l'eta' odierna vi rientra. Le categorie gia' confermate non si verificano di nuovo.
 */
export function verificaCategorie(
  confermate: string[], giaConfermate: string[], catalogo: Voce[], eta: number | null,
): Errori {
  const e: Errori = {}
  if (eta === null) return e
  for (const codice of confermate) {
    if (giaConfermate.includes(codice)) continue
    const voce = catalogo.find((v) => v.codice === codice)
    if (!voce || (voce.eta_min == null && voce.eta_max == null)) continue
    if (eta < (voce.eta_min ?? 0) || eta > (voce.eta_max ?? 200)) {
      e.categorie = `La categoria «${voce.nome}» non corrisponde all’età della persona (${eta} anni).`
      break
    }
  }
  return e
}

// --- corpi delle richieste -------------------------------------------------------------------

type Corpo = Record<string, unknown>

/** Ordine del catalogo: i confronti tra corpi non dipendono dall'ordine in cui si e' spuntato. */
function perCatalogo(codici: string[], voci: Voce[] | undefined): string[] {
  const posizione = new Map((voci ?? []).map((v, i) => [v.codice, i]))
  return [...codici].sort((a, b) => (posizione.get(a) ?? 999) - (posizione.get(b) ?? 999) || a.localeCompare(b))
}

/**
 * Corpo di POST /persone e base di PATCH /persone/:id. Le sezioni dipendono dal tipo di persona
 * (D-07): per chi non rappresenta un ente gli enti e l'ambito escono nulli; la condizione giuridica
 * ha il suo corpo, che il chiamante aggiunge solo per chi non rappresenta un ente.
 */
export function corpoAnagrafica(s: StatoAnagrafica, cataloghi: CataloghiRegole): Corpo {
  const operatore = tipoOperatore(s.tipo)
  // 481: con i dati anonimizzati i dati identificativi escono nulli e il motivo dell'assenza del
  // codice fiscale lo scrive l'API; le iniziali si mandano in maiuscolo.
  const anonima = s.dati_anonimizzati
  const nascita = s.modo_nascita === 'data'
    ? {
        data_nascita: s.data_nascita || null,
        // 460: la data da verificare diventa esatta solo con la conferma di chi l'ha controllata.
        precisione_nascita: s.precisione_registrata === 'da_verificare'
          ? (s.nascita_verificata ? 'esatta' : 'da_verificare')
          : s.precisione_registrata === 'stimata' ? 'stimata' : 'esatta',
        anno_nascita: null, fascia_eta_manuale: null,
      }
    : s.modo_nascita === 'anno'
      ? { data_nascita: null, precisione_nascita: 'solo_anno', anno_nascita: intero(s.anno_nascita), fascia_eta_manuale: null }
      : {
          data_nascita: null,
          precisione_nascita: s.precisione_registrata === 'da_verificare' && !s.nascita_verificata ? 'da_verificare' : 'ignota',
          anno_nascita: null, fascia_eta_manuale: s.fascia_eta_manuale || null,
        }
  const cf = anonima ? '' : s.cf.replace(/\s+/g, '').toUpperCase()
  const nome = (testo: string) => (anonima ? normalizzaIniziali(testo) : testo.trim())
  return {
    tipo: s.tipo,
    dati_anonimizzati: anonima,
    sede_riferimento: s.sede_riferimento || null,
    canali_conoscenza: perCatalogo(s.canali_conoscenza, cataloghi.canale_conoscenza).map((codice) => ({
      codice,
      // Il testo appartiene alla voce che lo chiede («altro»): le altre voci non ne hanno.
      specifica: haAttributo(cataloghi, 'canale_conoscenza', codice, 'richiede_specifica')
        ? oppureNullo(s.canale_specifica) : null,
    })),
    cognome: nome(s.cognome),
    nome: nome(s.nome),
    genere_codice: s.genere_codice || null,
    paese_nascita: s.paese_nascita || null,
    comune_nascita: anonima ? null : s.comune_nascita.codice || null,
    luogo_nascita_estero: anonima ? null : oppureNullo(s.luogo_nascita_estero),
    ...nascita,
    cittadinanza: s.cittadinanza || null,
    residenza_comune: s.residenza_comune.codice || null,
    residenza_indirizzo: anonima ? null : oppureNullo(s.residenza_indirizzo),
    // D-13: senza la spunta il domicilio non si registra.
    domicilio_diverso: s.domicilio_diverso,
    domicilio_comune: s.domicilio_diverso ? s.domicilio_comune.codice || null : null,
    domicilio_indirizzo: s.domicilio_diverso && !anonima ? oppureNullo(s.domicilio_indirizzo) : null,
    telefono: anonima ? null : oppureNullo(s.telefono),
    email: anonima ? null : oppureNullo(s.email),
    cf: cf || null,
    motivo_assenza_cf_codice: cf || anonima ? null : s.motivo_assenza_cf_codice || null,
    // La specifica solo per «in attesa di altro»; per le altre voci il testo lo scrive l'API.
    motivo_assenza_cf: cf || anonima || s.motivo_assenza_cf_codice !== 'ATTESA_ALTRO' ? null : oppureNullo(s.motivo_assenza_cf),
    lingua_madre: s.lingua_madre || null,
    lingue_veicolari: [...s.lingue_veicolari].sort(),
    livello_italiano: operatore ? null : s.livello_italiano || null,
    ente_appartenenza_id: operatore ? s.ente_appartenenza.id || null : null,
    sede_appartenenza_id: operatore ? s.sede_appartenenza || null : null,
    ente_operativo_id: operatore ? s.ente_operativo.id || null : null,
    ambito_professionale: operatore ? s.ambito_professionale || null : null,
    ambito_professionale_specifica: operatore ? oppureNullo(s.ambito_professionale_specifica) : null,
  }
}

export function corpoCondizione(s: StatoCondizione): Corpo {
  return {
    condizione_giuridica: s.condizione_giuridica || null,
    motivo_permesso: s.motivo_permesso || null,
    permesso_scadenza: s.permesso_scadenza || null,
  }
}

/** Corpo di POST /persone/:id/situazioni/lavorativa. */
export function corpoFormazione(s: StatoFormazione): Corpo {
  return {
    anni_studio: intero(s.anni_studio),
    titolo_studio: s.titolo_studio || null,
    riconoscimento_titolo: s.titolo_studio && s.titolo_studio !== 'NESSUNO' ? s.riconoscimento_titolo || null : null,
    situazione: s.situazione || null,
    situazione_specifica: oppureNullo(s.situazione_specifica),
    settore: s.settore || null,
    settore_specifica: oppureNullo(s.settore_specifica),
    lavoro_paese_origine: s.lavoro_paese_origine || null,
    lavoro_paese_origine_specifica: oppureNullo(s.lavoro_paese_origine_specifica),
    ...s.conservati,
  }
}

/** Corpo di PUT /persone/:id/servizi-bancari. */
export function corpoServizi(s: StatoServizi, cataloghi: CataloghiRegole): Corpo {
  return {
    voci: perCatalogo(s.voci, cataloghi.strumento_finanziario),
    motivo: s.motivo || null,
    motivo_specifica: oppureNullo(s.motivo_specifica),
  }
}

/** Corpo di POST /persone/:id/situazioni/abitativa. */
export function corpoAbitativa(s: StatoAbitativa): Corpo {
  return {
    stato_civile: s.stato_civile || null,
    figli_italia_totale: intero(s.figli_italia_totale),
    figli_italia_minorenni: intero(s.figli_italia_minorenni),
    figli_italia_maggiorenni: intero(s.figli_italia_maggiorenni),
    figli_estero_totale: intero(s.figli_estero_totale),
    figli_estero_minorenni: intero(s.figli_estero_minorenni),
    figli_estero_maggiorenni: intero(s.figli_estero_maggiorenni),
    soluzione_abitativa: s.soluzione_abitativa || null,
    nucleo_familiare: s.nucleo_familiare || null,
    nucleo_familiare_specifica: oppureNullo(s.nucleo_familiare_specifica),
    persone_conviventi: intero(s.persone_conviventi),
  }
}

/** Vero se nessun campo del corpo e' valorizzato: una sezione vuota non si registra. */
export function corpoVuoto(corpo: Corpo): boolean {
  return Object.values(corpo).every((v) => v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length))
}

const uguali = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

/** Campi che l'API tratta insieme: se ne cambia uno si mandano tutti. */
const GRUPPI = [
  ['data_nascita', 'anno_nascita', 'precisione_nascita', 'fascia_eta_manuale'],
  ['domicilio_diverso', 'domicilio_comune', 'domicilio_indirizzo'],
]

/**
 * Corpo di PATCH /persone/:id: solo i campi cambiati rispetto allo stato di partenza. Un campo
 * non inviato non cambia, quindi il codice fiscale mascherato o un dato che l'utente non vede
 * restano come sono.
 */
export function differenze(dopo: Corpo, prima: Corpo): Corpo {
  const fuori: Corpo = {}
  for (const [campo, valore] of Object.entries(dopo)) {
    if (!uguali(valore, prima[campo])) fuori[campo] = valore
  }
  for (const gruppo of GRUPPI) {
    if (gruppo.some((campo) => campo in fuori)) for (const campo of gruppo) fuori[campo] = dopo[campo]
  }
  // 481: attivando o togliendo «Dati anonimizzati» nome e cognome viaggiano con la casella: l'API li
  // verifica (iniziali, oppure completati) anche quando non cambiano.
  if ('dati_anonimizzati' in fuori) {
    fuori.nome = dopo.nome
    fuori.cognome = dopo.cognome
  }
  return fuori
}

export const corpiUguali = uguali

// --- errori dell'API sul campo ---------------------------------------------------------------

/** Forma dell'errore che l'API restituisce (servizi/api.ts, ErroreApi). */
export type ErroreRicevuto = {
  codice?: string
  message: string
  dettagli?: { campo: string; messaggio: string }[]
}

const CAMPO_PER_CODICE: Record<string, string> = {
  CF_NON_VALIDO: 'cf',
  CF_MANCANTE: 'motivo_assenza_cf_codice',
  MOTIVO_CF: 'motivo_assenza_cf_codice',
  MOTIVO_CF_SPECIFICA: 'motivo_assenza_cf',
  LIVELLO_ITALIANO: 'livello_italiano',
  NASCITA_PRECISIONE: 'modo_nascita',
  NASCITA_COMUNE_SOLO_ITALIA: 'comune_nascita',
  NASCITA_LUOGO_SOLO_ESTERO: 'luogo_nascita_estero',
  FASCIA_ETA_MANUALE: 'fascia_eta_manuale',
  DOMICILIO_NON_DIVERSO: 'domicilio_comune',
  ENTE_APPARTENENZA_OBBLIGATORIO: 'ente_appartenenza_id',
  ENTE_INESISTENTE: 'ente_appartenenza_id',
  APPARTENENZA_DOPPIA: 'sede_appartenenza',
  CAMPI_SOLO_OPERATORE: 'tipo',
  SEZIONE_NON_PREVISTA: 'tipo',
  // 481: dati anonimizzati. Gli errori con il dettaglio arrivano sul campo del dettaglio.
  INIZIALI: 'nome',
  NOME_COMPLETO: 'nome',
  ANONIMIZZATI_SOLO_ASSISTITI: 'tipo',
  DATO_IDENTIFICATIVO: 'dati_anonimizzati',
  DATI_IDENTIFICATIVI_PRESENTI: 'dati_anonimizzati',
  SEDE_INESISTENTE: 'sede_riferimento',
  MOTIVO_NON_AMMESSO: 'motivo_permesso',
  NESSUNO_ESCLUSIVO: 'voci',
  MOTIVO_SOLO_CON_NESSUNO: 'motivo',
  SETTORE_NON_AMMESSO: 'settore',
  CONVIVENTI_SOLO: 'persone_conviventi',
  CATEGORIA_INCOERENTE_ETA: 'categorie',
  DATE_INCOERENTI: 'conclusa_il',
  VULNERABILITA_APERTA: 'codice',
  VULNERABILITA_PRESENTE: 'rilevata_il',
}

/** Il messaggio di SPECIFICA_NON_AMMESSA nomina la voce: da li' si ricava il campo. */
const SPECIFICHE: [string, string][] = [
  ['ambito professionale', 'ambito_professionale_specifica'],
  ['canale', 'canali_conoscenza'],
  ['Altro motivo', 'motivo_specifica'],
  ['situazione lavorativa', 'situazione_specifica'],
  ['settore', 'settore_specifica'],
  ['Paese di origine', 'lavoro_paese_origine_specifica'],
  ['Con chi vive', 'nucleo_familiare_specifica'],
]

/** Nomi dei campi nelle richieste che non coincidono con il campo del modulo. */
const CAMPO_PER_NOME: Record<string, string> = {
  confermate: 'categorie',
  codici: 'codice',
}

/**
 * Campo del modulo a cui riferire un errore dell'API, con il messaggio da scrivere accanto.
 * Null se l'errore non riguarda un campo (perimetro, doppione, guasto): va mostrato in testa.
 */
export function campoDiErrore(errore: ErroreRicevuto): { campo: string; messaggio: string } | null {
  const dettaglio = errore.dettagli?.[0]
  if (dettaglio) {
    const nome = dettaglio.campo.split('.')[0]
    return { campo: CAMPO_PER_NOME[nome] ?? nome, messaggio: 'Valore non valido.' }
  }
  switch (errore.codice) {
    case undefined:
      return null
    case 'VOCE_NON_VALIDA': {
      const nome = /campo ([a-z_]+)/.exec(errore.message)?.[1]
      return nome ? { campo: CAMPO_PER_NOME[nome] ?? nome, messaggio: 'Voce non più disponibile: sceglierne un’altra.' } : null
    }
    case 'SPECIFICA_NON_AMMESSA': {
      const trovata = SPECIFICHE.find(([parola]) => errore.message.includes(parola))
      return trovata ? { campo: trovata[1], messaggio: errore.message } : null
    }
    case 'FIGLI_TOTALE':
      return { campo: errore.message.includes('in Italia') ? 'figli_italia_totale' : 'figli_estero_totale', messaggio: errore.message }
    default: {
      const campo = CAMPO_PER_CODICE[errore.codice]
      return campo ? { campo, messaggio: errore.message } : null
    }
  }
}

// --- presentazione ---------------------------------------------------------------------------

export const NOMI_MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto',
  'settembre', 'ottobre', 'novembre', 'dicembre']

/** Motivo della proposta di una categoria (core.categorie_proposte e righe da confermare). */
export const MOTIVI_PROPOSTA: Record<string, string> = {
  eta: 'proposta per età',
  genere: 'proposta per genere',
  tipo: 'proposta per tipo di persona',
  registrata: 'da confermare',
}
